import type { TFunction } from 'i18next'
import type { SaasApiError } from '../../../api'

const CJK_RE = /[\u4e00-\u9fff]/

function looksLikeInternalTechnicalMessage(message: string): boolean {
  const trimmed = message.trim()
  if (!trimmed) return false
  if (CJK_RE.test(trimmed)) return true
  if (/traceback|exception|error:\s/i.test(trimmed)) return true
  return false
}

/** Tenant-facing copy for Virtual Try-On submit/runtime errors. */
export function localizeVirtualTryOnSubmitError(
  err: Pick<SaasApiError, 'code' | 'message' | 'status'> | null | undefined,
  t: TFunction<'canvas'>,
): string {
  if (!err) return t('virtualTryOn.errors.processingFailed')

  const code = (err.code || '').trim()

  // VistoeraTASK-00263-20260928-1824-005 (P0 AI Credit Gate audit) —
  // handle InsufficientCreditsError explicitly BEFORE the generic
  // CJK-message suppression below, matching voiceTemplateError.ts /
  // generateAudioError.ts / motionReconstructionErrors.ts (task spec
  // §七 — tenants must see an explicit "insufficient credits" message,
  // never the generic fallback).
  if (code === 'member_credit_limit_exceeded') {
    return t('generation.errors.memberCreditLimitExceeded')
  }
  if (code === 'insufficient_credits' || err.status === 402) {
    return t('virtualTryOn.errors.pointsInsufficient')
  }

  if (code) {
    const localized = t(`virtualTryOn.errors.codes.${code}`, { defaultValue: '' })
    if (localized) return localized
  }

  const message = (err.message || '').trim()
  if (message && !looksLikeInternalTechnicalMessage(message)) {
    return message
  }

  return t('virtualTryOn.errors.processingFailed')
}

export function localizeVirtualTryOnTaskError(
  errorCode: string | null | undefined,
  t: TFunction<'canvas'>,
): string {
  return localizeVirtualTryOnSubmitError({ code: errorCode || '', message: '', status: 0 }, t)
}
